import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { PageHeader, Pill, SectionCard } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Plus, RefreshCw } from "lucide-react";

const OUTPUT_CATEGORIES = [
  { value: "PRIMARY_GOODS", label: "Primary goods" },
  { value: "COPRODUCT_NAKKU", label: "Coproduct — nakku" },
  { value: "REJECTION", label: "Rejection" },
  { value: "BRAN", label: "Bran" },
];

const DEST_BLENDED = "BLENDED_INTO_ACTIVE_BATCH";
const DEST_DEDICATED = "DEDICATED_REPROCESS_BATCH";

function qtlFromBags(bags, kgPerBag) {
  const b = Number(bags) || 0;
  const w = Number(kgPerBag) || 0;
  if (b <= 0 || w <= 0) return 0;
  return Math.round(((b * w) / 100) * 1000) / 1000;
}

function fmtQtl(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function todayLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isOpenStatus(status) {
  const s = String(status || "").toUpperCase();
  return s === "PLANNED" || s === "IN_PROGRESS" || s === "IN_PROCESS";
}

function statusPill(status) {
  const s = String(status || "").toUpperCase();
  if (s === "RECONCILED_COMPLETED" || s === "COMPLETED") return "success";
  if (s === "IN_PROGRESS" || s === "IN_PROCESS") return "warning";
  return "default";
}

function ProgressBar({ pct }) {
  const width = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="h-2 rounded-full bg-muted overflow-hidden border border-border/60">
      <div className="h-full bg-primary transition-all" style={{ width: `${width}%` }} />
    </div>
  );
}

export default function RiceReprocessing() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [runs, setRuns] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [opsTab, setOpsTab] = useState("cutting");

  // New run form
  const [showCreate, setShowCreate] = useState(false);
  const [destinationType, setDestinationType] = useState(DEST_BLENDED);
  const [targetBatchId, setTargetBatchId] = useState("");
  const [remarks, setRemarks] = useState("");

  // Cutting form
  const [cutDate, setCutDate] = useState(todayLocal());
  const [cutStockKey, setCutStockKey] = useState("");
  const [cutBags, setCutBags] = useState("");

  // Output form
  const [outDate, setOutDate] = useState(todayLocal());
  const [outCategory, setOutCategory] = useState("PRIMARY_GOODS");
  const [outItemId, setOutItemId] = useState("");
  const [outPack, setOutPack] = useState("50");
  const [outBags, setOutBags] = useState("");
  const [outTargetId, setOutTargetId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stockRes, itemRes, batchRes, histRes] = await Promise.all([
        api.get("/production/reprocessing/available-stocks"),
        api.get("/production/masters/items"),
        api.get("/production/batches", { params: { limit: 200 } }),
        api.get("/production/reprocessing", { params: { limit: 50 } }),
      ]);
      setStocks(stockRes.data?.stocks || []);
      setItems(itemRes.data?.results || []);
      setBatches(batchRes.data?.results || []);
      const list = histRes.data?.results || [];
      setRuns(list);
      if (activeId) {
        const found = list.find((r) => r.id === activeId);
        if (found) setDetail(found);
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [activeId]);

  useEffect(() => {
    load();
  }, [load]);

  const openRuns = useMemo(() => runs.filter((r) => isOpenStatus(r.status)), [runs]);
  const closedRuns = useMemo(() => runs.filter((r) => !isOpenStatus(r.status)), [runs]);
  const activeBatches = useMemo(
    () =>
      (batches || []).filter((b) => {
        const s = String(b.status || "").toUpperCase();
        return s && s !== "ARCHIVED_COMPLETED" && s !== "CLOSED";
      }),
    [batches]
  );

  const selectedStock = useMemo(
    () => stocks.find((s) => String(s.id) === String(cutStockKey)),
    [stocks, cutStockKey]
  );
  const cutQtl = useMemo(
    () => qtlFromBags(cutBags, selectedStock?.weight_per_bag || selectedStock?.pack_size_kg),
    [cutBags, selectedStock]
  );
  const outQtl = useMemo(() => qtlFromBags(outBags, outPack), [outBags, outPack]);

  const selectRun = async (id) => {
    setActiveId(id);
    try {
      const res = await api.get(`/production/reprocessing/${id}`);
      setDetail(res.data);
      if (res.data?.target_production_batch_id) {
        setOutTargetId(String(res.data.target_production_batch_id));
      }
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const createRun = async () => {
    if (destinationType === DEST_BLENDED && !targetBatchId) {
      toast.error("Select the active milling batch to blend into");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/production/reprocessing/create", {
        destination_type: destinationType,
        target_production_batch_id: targetBatchId ? Number(targetBatchId) : null,
        remarks: remarks || "",
      });
      toast.success(`Started ${res.data.batch_number}`);
      setShowCreate(false);
      setRemarks("");
      await load();
      if (res.data?.id) await selectRun(res.data.id);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCutting = async () => {
    if (!detail?.id) {
      toast.error("Select an active reprocessing run first");
      return;
    }
    if (!selectedStock) {
      toast.error("Select source stock to cut");
      return;
    }
    const bags = Number(cutBags) || 0;
    if (bags <= 0) {
      toast.error("Enter bags cut today");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/production/reprocessing/cutting-log", {
        reprocessing_batch_id: detail.id,
        cutting_date: cutDate,
        source_production_batch_id: selectedStock.batch_id,
        item_id: selectedStock.item_id,
        pack_size_kg: selectedStock.weight_per_bag || selectedStock.pack_size_kg || 0,
        bags_cut: bags,
        quintals_cut: cutQtl,
      });
      toast.success(`Cut ${bags} bags logged`);
      setCutBags("");
      setDetail(res.data.batch);
      await load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitOutput = async () => {
    if (!detail?.id) {
      toast.error("Select an active reprocessing run first");
      return;
    }
    if (!outItemId) {
      toast.error("Select output item");
      return;
    }
    const bags = Number(outBags) || 0;
    if (bags <= 0) {
      toast.error("Enter bags packed / blended today");
      return;
    }
    const target =
      outTargetId ||
      detail.target_production_batch_id ||
      (destinationType === DEST_BLENDED ? targetBatchId : "");
    setBusy(true);
    try {
      const res = await api.post("/production/reprocessing/output-log", {
        reprocessing_batch_id: detail.id,
        output_date: outDate,
        item_id: Number(outItemId),
        pack_size_kg: Number(outPack) || 0,
        bags_packed: bags,
        quintals_packed: outQtl,
        output_category: outCategory,
        target_production_batch_id: target ? Number(target) : null,
      });
      toast.success(`Output ${bags} bags logged`);
      setOutBags("");
      setDetail(res.data.batch);
      await load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const reconcile = async () => {
    if (!detail?.id) return;
    setBusy(true);
    try {
      const res = await api.post(`/production/reprocessing/${detail.id}/reconcile`, {});
      toast.success(`${res.data.batch_number} reconciled`);
      setDetail(res.data);
      await load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !runs.length) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading reprocessing…
      </div>
    );
  }

  const prog = detail?.progress || {};

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="rice-reprocessing-page">
      <PageHeader
        title="Rice reprocessing & blending"
        description="Multi-day cutting feed, in-line blend into active batches, then reconcile"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={busy}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setShowCreate((v) => !v)} disabled={busy}>
              <Plus className="h-4 w-4 mr-1" /> New run
            </Button>
          </div>
        }
      />

      {showCreate ? (
        <SectionCard title="Start reprocessing run" testId="reprocess-create">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Destination</Label>
              <Select value={destinationType} onValueChange={setDestinationType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEST_BLENDED}>Blend into active milling batch</SelectItem>
                  <SelectItem value={DEST_DEDICATED}>Dedicated reprocess batch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                {destinationType === DEST_BLENDED ? "Active milling batch (e.g. B-13)" : "Optional target batch"}
              </Label>
              <Select value={targetBatchId || "__none__"} onValueChange={(v) => setTargetBatchId(v === "__none__" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select batch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {activeBatches.map((b) => (
                    <SelectItem key={b.id} value={String(b.id)}>
                      {b.code} · {b.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Remarks</Label>
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={createRun} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Create run
            </Button>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
          </div>
        </SectionCard>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-4 space-y-4">
          <SectionCard title="Active runs" testId="reprocess-active">
            {!openRuns.length ? (
              <p className="text-sm text-muted-foreground">No in-progress runs. Start a new run to log daily cutting.</p>
            ) : (
              <ul className="space-y-3">
                {openRuns.map((r) => {
                  const p = r.progress || {};
                  const selected = activeId === r.id;
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => selectRun(r.id)}
                        className={`w-full text-left rounded-md border p-3 transition-colors ${
                          selected
                            ? "border-primary bg-primary/5"
                            : "border-border bg-card hover:bg-muted/40"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-sm font-semibold text-foreground">{r.batch_number}</span>
                          <Pill tone={statusPill(r.status)}>{r.status}</Pill>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {r.destination_type === DEST_BLENDED
                            ? `Blend → ${r.target_batch_code || "—"}`
                            : "Dedicated reprocess"}
                        </p>
                        <div className="mt-2 space-y-1">
                          <div className="flex justify-between text-[11px] text-muted-foreground">
                            <span>Cut {fmtQtl(p.total_cut_qtl)} qtl</span>
                            <span>Out {fmtQtl(p.total_packed_qtl)} qtl</span>
                          </div>
                          <ProgressBar pct={p.progress_pct} />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>

          <SectionCard title="Recently reconciled" testId="reprocess-history">
            {!closedRuns.length ? (
              <p className="text-sm text-muted-foreground">No closed runs yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {closedRuns.slice(0, 8).map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 border-b border-border/50 pb-2">
                    <button type="button" className="font-mono text-primary underline" onClick={() => selectRun(r.id)}>
                      {r.batch_number}
                    </button>
                    <span className="text-xs text-muted-foreground">{fmtQtl(r.total_input_qty_qtl)} qtl in</span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>

        <div className="xl:col-span-8 space-y-4">
          {!detail ? (
            <SectionCard title="Daily operations">
              <p className="text-sm text-muted-foreground">
                Select an active run on the left, or create a new run. Log cutting and packing day-by-day — stock moves
                immediately; reconcile when the plant run is finished.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Packing register: <Link className="text-primary underline" to="/production/packing">open packing</Link>
              </p>
            </SectionCard>
          ) : (
            <>
              <SectionCard
                title={`${detail.batch_number}`}
                testId="reprocess-detail-head"
                action={<Pill tone={statusPill(detail.status)}>{detail.status}</Pill>}
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Destination</p>
                    <p className="font-medium text-foreground">
                      {detail.destination_type === DEST_BLENDED ? "Blended" : "Dedicated"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Target batch</p>
                    <p className="font-medium font-mono text-foreground">{detail.target_batch_code || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total cut</p>
                    <p className="font-medium tabular text-foreground">
                      {prog.total_cut_bags || 0} bags · {fmtQtl(prog.total_cut_qtl)} qtl
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total packed</p>
                    <p className="font-medium tabular text-foreground">
                      {prog.total_packed_bags || 0} bags · {fmtQtl(prog.total_packed_qtl)} qtl
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <ProgressBar pct={prog.progress_pct} />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Output vs cut {Number(prog.progress_pct || 0).toFixed(1)}% · Process loss (live){" "}
                    {fmtQtl(detail.process_loss_qtl)} qtl ({detail.process_loss_pct || 0}%)
                  </p>
                </div>
              </SectionCard>

              {isOpenStatus(detail.status) ? (
                <SectionCard title="Daily operations panel" testId="reprocess-ops">
                  <Tabs value={opsTab} onValueChange={setOpsTab}>
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="cutting">Log cutting</TabsTrigger>
                      <TabsTrigger value="output">Log output</TabsTrigger>
                      <TabsTrigger value="reconcile">Reconcile</TabsTrigger>
                    </TabsList>

                    <TabsContent value="cutting" className="space-y-4 mt-4">
                      <p className="text-xs text-muted-foreground">
                        Debits source stock immediately (e.g. 10 bags Rejection Wand from B-01 today).
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Cutting date</Label>
                          <Input type="date" value={cutDate} onChange={(e) => setCutDate(e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Bags cut today</Label>
                          <Input
                            type="number"
                            min="0"
                            value={cutBags}
                            onChange={(e) => setCutBags(e.target.value)}
                            placeholder="e.g. 10"
                          />
                        </div>
                        <div className="md:col-span-2 space-y-2">
                          <Label>Source stock</Label>
                          <Select value={cutStockKey || "__none__"} onValueChange={(v) => setCutStockKey(v === "__none__" ? "" : v)}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select finished stock line" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">—</SelectItem>
                              {stocks.map((s) => (
                                <SelectItem key={s.id} value={String(s.id)}>
                                  {s.batch_label || s.batch_code} · {s.item_name} · {s.weight_per_bag} kg · avail{" "}
                                  {s.quantity_bags_available ?? s.bags_available}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">≈ {fmtQtl(cutQtl)} qtl</p>
                      <Button onClick={submitCutting} disabled={busy}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Submit cutting log
                      </Button>
                    </TabsContent>

                    <TabsContent value="output" className="space-y-4 mt-4">
                      <p className="text-xs text-muted-foreground">
                        Credits target inventory immediately. For in-line blending, choose the active milling batch.
                        Floor packing desk can also log this on{" "}
                        <Link to="/production/packing-register" className="text-primary underline">
                          Packing register → Reprocess output
                        </Link>
                        .
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Output date</Label>
                          <Input type="date" value={outDate} onChange={(e) => setOutDate(e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Category</Label>
                          <Select value={outCategory} onValueChange={setOutCategory}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {OUTPUT_CATEGORIES.map((c) => (
                                <SelectItem key={c.value} value={c.value}>
                                  {c.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Item</Label>
                          <Select value={outItemId || "__none__"} onValueChange={(v) => setOutItemId(v === "__none__" ? "" : v)}>
                            <SelectTrigger>
                              <SelectValue placeholder="Output item" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">—</SelectItem>
                              {items.map((it) => (
                                <SelectItem key={it.id} value={String(it.id)}>
                                  {it.name} ({it.code})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Pack kg/bag</Label>
                          <Input value={outPack} onChange={(e) => setOutPack(e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Bags packed / blended</Label>
                          <Input
                            type="number"
                            min="0"
                            value={outBags}
                            onChange={(e) => setOutBags(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Credit to production batch</Label>
                          <Select
                            value={outTargetId || String(detail.target_production_batch_id || "") || "__none__"}
                            onValueChange={(v) => setOutTargetId(v === "__none__" ? "" : v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Target batch" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">—</SelectItem>
                              {activeBatches.map((b) => (
                                <SelectItem key={b.id} value={String(b.id)}>
                                  {b.code}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">≈ {fmtQtl(outQtl)} qtl</p>
                      <Button onClick={submitOutput} disabled={busy}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Submit output log
                      </Button>
                    </TabsContent>

                    <TabsContent value="reconcile" className="space-y-4 mt-4">
                      <div className="overflow-x-auto border border-border rounded-md">
                        <table className="w-full text-xs min-w-[480px]">
                          <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
                            <tr>
                              <th className="text-left py-2 px-2">Date</th>
                              <th className="text-right py-2 px-2">Cut bags</th>
                              <th className="text-right py-2 px-2">Cut qtl</th>
                              <th className="text-right py-2 px-2">Out bags</th>
                              <th className="text-right py-2 px-2">Out qtl</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail.daily_summary || []).length === 0 ? (
                              <tr>
                                <td colSpan={5} className="py-3 px-2 text-muted-foreground">
                                  No daily logs yet.
                                </td>
                              </tr>
                            ) : (
                              detail.daily_summary.map((d) => (
                                <tr key={d.date} className="border-t border-border/60">
                                  <td className="py-1.5 px-2 tabular">{d.date}</td>
                                  <td className="py-1.5 px-2 text-right tabular">{d.cut_bags}</td>
                                  <td className="py-1.5 px-2 text-right tabular">{fmtQtl(d.cut_qtl)}</td>
                                  <td className="py-1.5 px-2 text-right tabular">{d.out_bags}</td>
                                  <td className="py-1.5 px-2 text-right tabular">{fmtQtl(d.out_qtl)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-border bg-muted/30 font-medium">
                              <td className="py-2 px-2">Cumulative</td>
                              <td className="py-2 px-2 text-right tabular">{prog.total_cut_bags || 0}</td>
                              <td className="py-2 px-2 text-right tabular">{fmtQtl(prog.total_cut_qtl)}</td>
                              <td className="py-2 px-2 text-right tabular">{prog.total_packed_bags || 0}</td>
                              <td className="py-2 px-2 text-right tabular">{fmtQtl(prog.total_packed_qtl)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <div className="text-sm space-y-1 text-foreground">
                        <p>
                          Primary {fmtQtl(prog.primary_packed_qtl)} + coproducts {fmtQtl(prog.coproduct_packed_qtl)} +
                          loss {fmtQtl(detail.process_loss_qtl)} ≈ cut {fmtQtl(prog.total_cut_qtl)} qtl
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Closing posts the GL stock journal and queues Tally sync. Inventory was already moved on each
                          daily log.
                        </p>
                      </div>
                      <Button onClick={reconcile} disabled={busy || !(detail.cutting_logs || []).length}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Close &amp; reconcile batch
                      </Button>
                    </TabsContent>
                  </Tabs>
                </SectionCard>
              ) : (
                <SectionCard title="Reconciled summary">
                  <p className="text-sm text-foreground">
                    Closed by {detail.finalized_by || "—"} at {detail.finalized_at || "—"}. Loss{" "}
                    {fmtQtl(detail.process_loss_qtl)} qtl ({detail.process_loss_pct}%).
                    {detail.gl_journal_entry_id ? ` GL #${detail.gl_journal_entry_id}.` : ""}
                  </p>
                </SectionCard>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SectionCard title="Cutting log">
                  <div className="overflow-x-auto max-h-64">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase text-muted-foreground">
                        <tr>
                          <th className="text-left py-1">Date</th>
                          <th className="text-left py-1">Source</th>
                          <th className="text-right py-1">Bags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.cutting_logs || []).map((ln) => (
                          <tr key={ln.id} className="border-t border-border/50">
                            <td className="py-1 tabular">{ln.cutting_date}</td>
                            <td className="py-1">
                              {ln.source_batch_code} · {ln.item_name}
                            </td>
                            <td className="py-1 text-right tabular">{ln.bags_cut}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {!(detail.cutting_logs || []).length ? (
                      <p className="text-xs text-muted-foreground py-2">No cutting logs.</p>
                    ) : null}
                  </div>
                </SectionCard>
                <SectionCard title="Output log">
                  <div className="overflow-x-auto max-h-64">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase text-muted-foreground">
                        <tr>
                          <th className="text-left py-1">Date</th>
                          <th className="text-left py-1">Item</th>
                          <th className="text-right py-1">Bags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.output_logs || []).map((ln) => (
                          <tr key={ln.id} className="border-t border-border/50">
                            <td className="py-1 tabular">{ln.output_date}</td>
                            <td className="py-1">
                              {ln.item_name} → {ln.target_batch_code || "—"}
                            </td>
                            <td className="py-1 text-right tabular">{ln.bags_packed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {!(detail.output_logs || []).length ? (
                      <p className="text-xs text-muted-foreground py-2">No output logs.</p>
                    ) : null}
                  </div>
                </SectionCard>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
